import * as Api from '@ViewModels';
import { StyleSheet, css } from 'aphrodite';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import {
	IVolunteerRole,
	useArchiveVolunteerRoleMutation,
	useCreateVolunteerRoleMutation,
	useUpdateVolunteerRoleMutation,
	useVolunteerRolesQuery,
} from '../../../queries/Volunteer';
import { Button } from '../../components-v2/Button';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../../components-v2/Drawer';
import { Input } from '../../components-v2/Input';
import { THEME } from '../../components-v2/Theme/theme';
import { TrashIcon } from '../../components/svgs/icons/TrashIcon';

interface IProps {
	isOpen: boolean;
	onClose: () => void;
	impersonationContext?: Api.IImpersonationContext;
}

interface IRoleRowProps {
	role: IVolunteerRole;
	isConfirmingArchive: boolean;
	isArchiving: boolean;
	onRename: (role: IVolunteerRole, name: string) => void;
	onRequestArchive: (role: IVolunteerRole) => void;
	onConfirmArchive: () => void;
	onCancelArchive: () => void;
}

const RoleRow = ({
	role,
	isConfirmingArchive,
	isArchiving,
	onRename,
	onRequestArchive,
	onConfirmArchive,
	onCancelArchive,
}: IRoleRowProps) => {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(role.name);
	const skipRenameOnBlur = useRef(false);

	// Confirm inline rather than in a centered modal: DeleteConfirmation's overlay (z-index 5)
	// renders behind the drawer's overlay (z-index 50), so a modal here would be hidden.
	if (isConfirmingArchive) {
		return (
			<div className={css(styles.confirmRow)}>
				<span className={css(styles.confirmText)}>Archive “{role.name}”? Shifts already using it keep it.</span>
				<div className={css(styles.confirmActions)}>
					<Button type='button' variant='destructive' size='sm' onClick={onConfirmArchive} isLoading={isArchiving}>
						Archive
					</Button>
					<Button type='button' variant='outline' size='sm' onClick={onCancelArchive} disabled={isArchiving}>
						Cancel
					</Button>
				</div>
			</div>
		);
	}

	if (editing) {
		return (
			<div className={css(styles.row)}>
				<Input
					value={draft}
					onChange={e => setDraft(e.target.value)}
					onBlur={() => {
						setEditing(false);
						if (skipRenameOnBlur.current) {
							skipRenameOnBlur.current = false;
							return;
						}
						onRename(role, draft);
					}}
					autoFocus
				/>
				<Button
					type='button'
					variant='ghost'
					size='icon'
					aria-label={`Archive ${role.name}`}
					onMouseDown={() => {
						skipRenameOnBlur.current = true;
					}}
					onClick={() => onRequestArchive(role)}
				>
					<TrashIcon width={16} height={16} />
				</Button>
			</div>
		);
	}

	return (
		<div className={css(styles.row)}>
			<span className={css(styles.roleName)} onClick={() => setEditing(true)}>
				{role.name}
			</span>
			<Button
				type='button'
				variant='ghost'
				size='icon'
				aria-label={`Archive ${role.name}`}
				onClick={() => onRequestArchive(role)}
			>
				<TrashIcon width={16} height={16} />
			</Button>
		</div>
	);
};

export const ManageJobsDrawer = ({ isOpen, onClose, impersonationContext }: IProps) => {
	const { data: roles = [] } = useVolunteerRolesQuery({ includeArchived: false, impersonationContext });
	const [newName, setNewName] = useState('');
	const [pendingArchive, setPendingArchive] = useState<IVolunteerRole | null>(null);

	const createMutation = useCreateVolunteerRoleMutation({
		onSuccess: () => setNewName(''),
		onError: error => toast.error(error.systemMessage || 'Failed to create the job'),
	});
	const updateMutation = useUpdateVolunteerRoleMutation({
		onError: error => toast.error(error.systemMessage || 'Failed to rename the job'),
	});
	const archiveMutation = useArchiveVolunteerRoleMutation({
		onSuccess: () => setPendingArchive(null),
		onError: error => toast.error(error.systemMessage || 'Failed to archive the job'),
	});

	const addJob = () => {
		if (!newName.trim()) {
			return;
		}
		createMutation.mutate({ role: { name: newName.trim() }, impersonationContext });
	};

	const rename = (role: IVolunteerRole, name: string) => {
		if (name.trim() && name.trim() !== role.name) {
			updateMutation.mutate({ role: { ...role, name: name.trim() }, impersonationContext });
		}
	};

	const confirmArchive = () => {
		if (pendingArchive) {
			archiveMutation.mutate({ roleId: pendingArchive.id, impersonationContext });
		}
	};

	return (
		<Drawer
			open={isOpen}
			onOpenChange={next => {
				if (!next) {
					onClose();
				}
			}}
			swipeDirection='right'
		>
			<DrawerContent>
				<DrawerHeader>
					<DrawerTitle>Manage jobs</DrawerTitle>
				</DrawerHeader>
				<div className={css(styles.body)}>
					<div className={css(styles.addRow)}>
						<Input value={newName} onChange={e => setNewName(e.target.value)} placeholder='New job name' />
						<Button type='button' onClick={addJob} isLoading={createMutation.isPending}>
							Add job
						</Button>
					</div>
					<div className={css(styles.list)}>
						{roles.map(role => (
							<RoleRow
								key={role.id}
								role={role}
								isConfirmingArchive={pendingArchive?.id === role.id}
								isArchiving={archiveMutation.isPending && pendingArchive?.id === role.id}
								onRename={rename}
								onRequestArchive={setPendingArchive}
								onConfirmArchive={confirmArchive}
								onCancelArchive={() => setPendingArchive(null)}
							/>
						))}
						{roles.length === 0 ? <div className={css(styles.empty)}>No jobs yet.</div> : null}
					</div>
				</div>
			</DrawerContent>
		</Drawer>
	);
};

const styles = StyleSheet.create({
	body: { display: 'flex', flexDirection: 'column', gap: THEME.spacing.md, padding: THEME.spacing.md },
	addRow: { display: 'flex', gap: THEME.spacing.sm },
	list: { display: 'flex', flexDirection: 'column', gap: THEME.spacing.sm },
	row: { alignItems: 'center', display: 'flex', gap: THEME.spacing.sm },
	roleName: {
		color: THEME.colors.text.main,
		cursor: 'pointer',
		flex: 1,
		fontSize: THEME.typography.fontSizes.md,
	},
	confirmRow: {
		alignItems: 'center',
		backgroundColor: THEME.colors.background.main,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.sm,
		justifyContent: 'space-between',
		padding: THEME.spacing.sm,
	},
	confirmText: {
		color: THEME.colors.text.main,
		flex: 1,
		fontSize: THEME.typography.fontSizes.sm,
		minWidth: 160,
	},
	confirmActions: { display: 'flex', gap: THEME.spacing.xs },
	empty: { color: THEME.colors.text.light, fontSize: THEME.typography.fontSizes.sm },
});
